import * as angular from 'angular';
import * as _ from 'lodash';

import {
    AcquisitionStrategy,
    ActualDataType,
    ComponentDefinition,
    ConnectionPoint,
    ContextualField,
    DigitalSet,
    FlatMode,
    GuidObjectType,
    IFrequency,
    KeyValuePair,
    Miniature,
    PropertyType,
    TagDefinition
} from '../../../models';
import { tagSourceConnectionPointTypes, EnumHelpers, guidEmpty, previousStepConectionPointTypes, digitalConnectionPointTypes, spotConnectionPointTypes, timestampConnectionPointTypes, stringConnectionPointTypes } from '../../../common';
import * as Fields from '../../../common/helpers/contextual';
import { frequencyErrors, periodValidation } from '../../../common/helpers/contextual';
import { getTagsMiniatures } from './getTagsMiniatures';

interface ITagsContextualFormController extends angular.IFormController {
    digitalSetGuidPropertyProperty: angular.INgModelController;
    identifierNameProperty: angular.INgModelController;
    identifierCommentProperty: angular.INgModelController;
    nameProperty: angular.INgModelController;
    unitProperty: angular.INgModelController;
    typeProperty: angular.INgModelController;
    subTypeProperty: angular.INgModelController;
    qualifierProperty: angular.INgModelController;
    minProperty: angular.INgModelController;
    maxProperty: angular.INgModelController;
    sourceProperty: angular.INgModelController;
}

class ModernTagsContextualController implements angular.IComponentController {
    allConnectionPoints: ConnectionPoint[];
    connectionPoints: ConnectionPoint[];
    digitalSets: DigitalSet[] = [];
    flatModes: KeyValuePair<number, string>[];
    formController: ITagsContextualFormController;
    isLoaded: boolean;
    isLoading: boolean;
    loadingMessage: string;
    miniatures: Miniature[];
    onlyStats: boolean;
    properties: ContextualField[];
    selectedItem: TagDefinition;
    tags: TagDefinition[];
    types = tagSourceConnectionPointTypes;

    onCancel: () => void;
    onSave: () => void;

    private _watchers: Function[] = [];

    static $inject = [
        '$scope',
        '$timeout'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$timeout: angular.ITimeoutService
    ) {
        this.flatModes = EnumHelpers.KeyValues(FlatMode);
    }

    $onInit() {
        this.connectionPoints = this.filterConnectionPoints(this.allConnectionPoints);
        if (this.allConnectionPoints) {
            this.miniatures = getTagsMiniatures(this.allConnectionPoints);
        }
        this._$scope.$watch(() => this.isLoaded, () => {
            if (this.isLoaded) {
                this.initProperties();
            }
        });
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['selectedItem'] && changesObj['selectedItem'].currentValue) {
            if (!this.selectedItem.name) {
                this.formController.$setDirty();
            }
            this.onItemChange();
            this.onSelectedItemChange();
        }

        if (changesObj['allConnectionPoints']) {
            this.connectionPoints = this.filterConnectionPoints(this.allConnectionPoints);
            this.miniatures = getTagsMiniatures(this.allConnectionPoints);
        }

        this.initProperties();
    }

    private onSelectedItemChange() {
        // clear watchers
        this._watchers.forEach(w => w());
        this._watchers = [];
        // reinit watchers
        this._watchers.push(this._$scope.$watch(
            () => this.selectedItem?.actualDataType,
            () => this.updateVisibility(this.properties)
        ));
    }

    private filterConnectionPoints(data: ConnectionPoint[]): ConnectionPoint[] {
        return data?.length > 0
            ? data.filter(cp => this.types.indexOf(cp.type) >= 0)
            : data;
    }

    private initProperties() {
        const nameField: ContextualField = angular.copy(Fields.Name);
        nameField.isValid = (newName: string) => {
            const isUnique = this.tags.filter(t => t.name === newName && t.guid !== this.selectedItem?.guid).length === 0;
            return { 'duplicate': isUnique };
        };

        const minField = angular.copy(Fields.Min);
        minField.onChange = () => this.validateMinMax();
        const maxField = angular.copy(Fields.Max);
        maxField.onChange = () => this.validateMinMax();

        const customPropertiesField: ContextualField = angular.copy(Fields.CustomProperties);
        customPropertiesField.extraData.type = GuidObjectType.Tag;

        const variablesFields = angular.copy(Fields.variableProperties);
        const digitalSetsField = angular.copy(Fields.DigitalSetGuid);
        const isFlatHidden = this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.Spot;

        const digitalSets = this.digitalSets.map(s => { return { key: s.guid, value: s.name }; });
        digitalSetsField.extraData = { options: digitalSets };
        digitalSetsField.isValid = (value: string) => {
            return {
                'required': !!value && value !== guidEmpty
            };
        };

        const properties: ContextualField[] = [
            nameField,
            angular.copy(Fields.ShortComment),
            angular.copy(Fields.LongComment),
            customPropertiesField,
            {
                modelName: 'acquisitionPointGuid',
                propertyName: 'acquisitionPointGuidProperty',
                propertyType: PropertyType.ConnectionPoint,
                placeholder: 'Source',
                label: 'Source',
                icon: 'fa-connectdevelop',
                extraData: {
                    types: this.types
                },
                onChange: () => this._$timeout(() => {
                    this.initProperties();
                    this._$timeout(() => this.formController['acquisitionPointGuidProperty'].$setDirty());
                })
            }, {
                modelName: 'importedPointID',
                propertyName: 'importedPointIDProperty',
                propertyType: PropertyType.Text,
                placeholder: 'Point ID',
                label: 'Point ID',
                iconText: 'ID',
                disabled: true
            },
            this.getStrategyField(),
            this.getDataTypeField(digitalSetsField),
            digitalSetsField,
        ]
            .concat(variablesFields)
            .concat([{
                modelName: 'importedUnit',
                propertyName: 'importedUnitProperty',
                propertyType: PropertyType.Text,
                placeholder: 'Imported unit',
                label: 'Imported unit',
                icon: 'fa-balance-scale',
                iconText: 'H',
                disabled: true
            }, {
                modelName: 'sourceUnitGuid',
                propertyName: 'sourceUnitGuidProperty',
                propertyType: PropertyType.UnitWithEmpty,
                placeholder: 'Source unit',
                label: 'Source unit',
                icon: 'fa-balance-scale',
                iconText: 'S',
                extraData: { variableTypeGuid: this.selectedItem?.variableTypeGuid },
                inlineHelp: 'Define only if you want to convert values from Source unit to Unit at acquisition time. Leave blank otherwise.'
            }, {
                modelName: 'validityFrequency',
                propertyName: 'validityProperty',
                propertyType: PropertyType.Frequency,
                placeholder: 'Validity period',
                label: 'Validity period',
                icon: 'fa-calendar-check-o',
                errorName: 'validityPropertyInterval',
                onChange: (_ignored, freq: IFrequency) => {
                    if (freq) {
                        this.selectedItem.validityInterval = freq.interval;
                        this.selectedItem.validityUnit = freq.intervalTimeUnit;
                    }
                },
                extraData: {
                    frequencyValidationRules: periodValidation
                },
                messages: frequencyErrors
            }, {
                modelName: 'lag',
                propertyName: 'lagProperty',
                propertyType: PropertyType.Frequency,
                placeholder: 'Offset',
                label: 'Offset',
                icon: 'fa-arrows-h',
                errorName: 'lagPropertyInterval',
                extraData: {
                    frequencyValidationRules: [
                        {
                            key: 'required',
                            isValid: (frequency: IFrequency) => {
                                return frequency.interval === null
                                    || frequency.interval === undefined
                                    || frequency.intervalTimeUnit >= 0;
                            }
                        }]
                },
                messages: frequencyErrors
            },
            angular.copy(Fields.DefaultValue),
            angular.copy(Fields.Resolution),
            angular.copy(Fields.ResolutionPercentage),
                minField,
                maxField,
            {
                modelName: 'stdDevThreshold',
                propertyName: 'stdDevThresholdProperty',
                propertyType: PropertyType.Number,
                placeholder: 'Std dev threshold',
                label: 'Std dev threshold',
                iconText: '⟿',
                messages: [
                    { key: 'float', value: 'Std dev threshold must be a number' }
                ]
            }, {
                modelName: 'flatMode',
                propertyName: 'flatModeProperty',
                propertyType: PropertyType.Select,
                placeholder: 'Flat mode',
                label: 'Flat mode',
                iconText: '―',
                hide: isFlatHidden,
                extraData: { options: this.flatModes }
            },
            // TODO cleansing (fa-shower)
            {
                modelName: 'isReviewRequired',
                propertyName: 'isReviewRequiredProperty',
                propertyType: PropertyType.Boolean,
                placeholder: 'Requires review',
                label: 'Requires review',
                icon: 'fa-exclamation-triangle',
            }, {
                modelName: 'isIgnored',
                propertyName: 'isIgnoredProperty',
                propertyType: PropertyType.Boolean,
                placeholder: 'Ignored',
                label: 'Ignored',
                icon: 'fa-eye-slash',
            }
            ]);

        if (this.selectedItem) {
            Fields.configureVariableTypeProperties(properties, this.selectedItem, ['sourceUnitGuid']);
        }
        this.updateVisibility(properties);
        this.properties = properties;
    }

    private updateVisibility(properties: ContextualField[]) {
        const isDigital = this.selectedItem?.actualDataType === ActualDataType.Digital;
        const isFlatHidden = this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.Spot;

        properties.forEach(p => p.hide = this.onlyStats);

        if (isDigital) {
            Fields.variableProperties
                .map(f => f.modelName)
                .forEach(name => this.showField(name, properties, false));
            this.showField('importedUnit', properties, false);
            this.showField('sourceUnitGuid', properties, false);
        } else {
            this.showField('digitalSetGuid', properties, false);
        }

        const isTimestamp = this.selectedItem?.actualDataType === ActualDataType.Timestamp;
        if (isTimestamp) {
            Fields.variableProperties
                .map(f => f.modelName)
                .forEach(name => this.showField(name, properties, false));
            this.showField('importedUnit', properties, false);
            this.showField('sourceUnitGuid', properties, false);
            this.showField('stdDevThreshold', properties, false);
            this.showField(Fields.Resolution.modelName, properties, false);
            this.showField(Fields.ResolutionPercentage.modelName, properties, false);
            this.showField(Fields.Min.modelName, properties, false);
            this.showField(Fields.Max.modelName, properties, false);
            this.showField(Fields.DefaultValue.modelName, properties, false);
        }

        const isString = this.selectedItem?.actualDataType === ActualDataType.String;
        if (isString) {
            Fields.variableProperties
                .map(f => f.modelName)
                .forEach(name => this.showField(name, properties, false));
            this.showField('importedUnit', properties, false);
            this.showField('sourceUnitGuid', properties, false);
            this.showField('stdDevThreshold', properties, false);
            this.showField(Fields.Resolution.modelName, properties, false);
            this.showField(Fields.ResolutionPercentage.modelName, properties, false);
            this.showField(Fields.Min.modelName, properties, false);
            this.showField(Fields.Max.modelName, properties, false);
        }

        if (this.onlyStats) {
            this.showField('stdDevThreshold', properties);
            this.showField(Fields.DefaultValue.modelName, properties);
            this.showField(Fields.Resolution.modelName, properties);
            this.showField(Fields.ResolutionPercentage.modelName, properties);
            this.showField(Fields.Min.modelName, properties);
            this.showField(Fields.Max.modelName, properties);
            this.showField('flatMode', properties);
        }
        this.showField('flatMode', properties, !isFlatHidden);
    }

    private showField(modelName: string, properties: ContextualField[], show: boolean = true) {
        const field = _.find(properties, p => p.modelName === modelName);
        if (field !== null) {
            field.hide = !show;
        }
    }

    private getStrategyField(): ContextualField {
        const field = angular.copy(Fields.AcquisitionStrategyField);
        const allOptions = field.extraData.options;

        let allowPreviousStep = false;
        let allowSpot = false;

        // remove non viable options for strategy according to selected connection point
        const cpGuid = this.selectedItem?.acquisitionPointGuid;
        if (cpGuid && cpGuid !== guidEmpty) {

            allowPreviousStep = _.some(this.connectionPoints, cp =>
                cp.guid === cpGuid && previousStepConectionPointTypes.indexOf(cp.type) >= 0
            );

            allowSpot = _.some(this.connectionPoints, cp =>
                cp.guid === cpGuid && spotConnectionPointTypes.indexOf(cp.type) >= 0
            );
        }

        const options = [field.extraData.options[AcquisitionStrategy.Default]];

        if (allowPreviousStep) {
            options.push(allOptions[AcquisitionStrategy.PreviousStep]);
        } else {
            // Reset value if option is not allowed
            if (this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.PreviousStep) {
                this.selectedItem.acquisitionStrategy = AcquisitionStrategy.Default;
                this.setDirty(field);
            }
        }

        if (allowSpot) {
            options.push(allOptions[AcquisitionStrategy.Spot]);
        } else {
            // Reset value if option is not allowed
            if (this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.Spot) {
                this.selectedItem.acquisitionStrategy = AcquisitionStrategy.Default;
                this.setDirty(field);
            }
        }

        field.extraData.options = options;

        // allow to recompute values according to strategy
        field.onChange = () => this._$timeout(() => {
            this.initProperties();
            this.setDirty(field);
        });

        return field;
    }

    private setDirty(field: ContextualField) {
        this._$timeout(() => this.formController[field.propertyName].$setDirty());
    }

    private getDataTypeField(digitalSetsField: ContextualField): ContextualField {
        const field = angular.copy(Fields.ActualDataTypeField);
        const cpGuid = this.selectedItem?.acquisitionPointGuid;
        const isStrategyAllowDigital = this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.Default
            || this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.Spot;
        const isStrategyAllowString = this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.Default
            || this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.Spot;
        const optionFloat = field.extraData.options[0];
        const optionDigital = field.extraData.options[1];
        const optionTimestamp = field.extraData.options[2];
        const optionString = field.extraData.options[3];

        // Set validation of field
        field.onChange = v => this._$timeout(() => {
            const currentField = this.formController[digitalSetsField.propertyName];
            if (currentField) {
                const value = currentField.$viewValue;
                const isEmpty = !value || value === guidEmpty;
                const isDigital = (v === ActualDataType.Digital);
                currentField.$setValidity('required', !(isDigital && isEmpty));
            }
        });

        // Compute dynamic configuration
        let allowDigital = false;
        let allowTimestamp = false;
        let allowString = false;
        if (cpGuid && cpGuid !== guidEmpty) {
            const cpSupportDigital = _.some(this.connectionPoints, cp =>
                cp.guid === cpGuid && digitalConnectionPointTypes.indexOf(cp.type) >= 0
            );
            const cpSupportString = _.some(this.connectionPoints, cp =>
                cp.guid === cpGuid && stringConnectionPointTypes.indexOf(cp.type) >= 0
            );
            allowDigital = isStrategyAllowDigital && cpSupportDigital;
            allowTimestamp = this.selectedItem?.acquisitionStrategy === AcquisitionStrategy.Default
                && this.connectionPoints
                    .filter(cp => cp.guid === cpGuid && timestampConnectionPointTypes.indexOf(cp.type) >= 0)
                    .length > 0;
            allowString = isStrategyAllowString && cpSupportString;
        }

        // Rest option field according to dynamic configuration
        const options = [optionFloat];
        if (allowDigital) {
            options.push(optionDigital);
        } else {
            if (this.selectedItem?.actualDataType === ActualDataType.Digital) {
                this.selectedItem.actualDataType = ActualDataType.Float;
                this.setDirty(field);
            }
        }

        if (allowTimestamp) {
            options.push(optionTimestamp);
        } else {
            if (this.selectedItem?.actualDataType === ActualDataType.Timestamp) {
                this.selectedItem.actualDataType = ActualDataType.Float;
                this.setDirty(field);
            }
        }

        if (allowString) {
            options.push(optionString);
        } else {
            if (this.selectedItem?.actualDataType === ActualDataType.String) {
                this.selectedItem.actualDataType = ActualDataType.Float;
                this.setDirty(field);
            }
        }

        field.extraData.options = options;
        return field;
    }

    private validateMinMax(): void {
        const min = this.formController[Fields.Min.propertyName].$viewValue;
        const max = this.formController[Fields.Max.propertyName].$viewValue;

        let minmax = true;
        if (min !== undefined && min !== '' && max !== undefined && max !== '') {
            const minNumber = parseFloat(min);
            const maxNumber = parseFloat(max);
            minmax = isNaN(minNumber)
                || isNaN(maxNumber)
                || parseFloat(min) < parseFloat(max);
        }
        this.formController[Fields.Min.propertyName].$setValidity('minmax', minmax);
        this.formController[Fields.Max.propertyName].$setValidity('minmax', minmax);
    }

    private onItemChange() {
        (this.selectedItem as any).validityFrequency = {
            interval: this.selectedItem.validityInterval,
            intervalTimeUnit: this.selectedItem.validityUnit
        };

        this.formController?.sourceProperty?.$setValidity(
            'invalid',
            this.connectionPoints.map(c => c.guid).indexOf(this.selectedItem?.acquisitionPointGuid) >= 0
        );
    }
}

export const wtModernTagsContextual: ComponentDefinition = {
    componentId: 'wtModernTagsContextual',
    controller: ModernTagsContextualController,
    template: require('./modernTagsContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        allConnectionPoints: '<',
        digitalSets: '<',
        formController: '=',
        onCancel: '&?',
        onSave: '&',
        onlyStats: '@?',
        selectedItem: '<',
        tags: '<',
        isLoaded: '<'
    }
};